import { useState } from 'react';
import { apiFetch } from '@/lib/apiClient';
import { suggestWorkspaceName } from '@/common/utils/workspace-name';
import { SetupScreenStyles } from './SetupScreenStyles';

interface WorkspaceNameSetupScreenProps {
  /** Active session token (the user is authenticated by the time this gate shows). */
  token: string | null;
  /** Owner's existing name (SSO display name when available), prefilled into the name field. */
  initialName?: string;
  /** True for team workspaces — adjusts the copy/placeholders. */
  isTeam?: boolean;
  /** Called once the names are saved (skipped=false) or the user skips (skipped=true). */
  onDone: (skipped: boolean) => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '11px 13px',
  border: '1px solid var(--color-border, #e8e9e3)',
  borderRadius: 9,
  fontSize: 15,
  fontFamily: 'inherit',
  background: 'var(--color-surface, #fff)',
  color: 'var(--color-text-primary, #2b2f27)',
  outline: 'none',
  boxSizing: 'border-box',
};

/**
 * First-run gate for a freshly-created workspace (#3949 team-only, generalized
 * to every plan tier in #3981). A new workspace is named "Personal" by
 * default, so without this the onboarding beat greets the owner with "Welcome
 * to Personal." This screen captures the owner's name + a real workspace name
 * (one PATCH /auth/profile call sets both) before the chat loads. SSO logins
 * arrive with `initialName` set (provider display name), so the personal
 * workspace field is prefilled with "<FirstName>'s Workspace" — one tap to
 * accept. Magic-link users (no captured name) get both fields blank with
 * placeholders. Skippable so it never hard-blocks; skip keeps "Personal".
 * Mounted as a gate in App.tsx.
 */
export function WorkspaceNameSetupScreen({
  token,
  initialName = '',
  isTeam = false,
  onDone,
}: WorkspaceNameSetupScreenProps) {
  const [name, setName] = useState(initialName);
  // Team workspaces get no suggestion (a person's name is a poor team name);
  // personal/solo workspaces are prefilled from the SSO display name.
  const [workspace, setWorkspace] = useState(() =>
    isTeam ? '' : suggestWorkspaceName(initialName),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = name.trim().length > 0 && workspace.trim().length > 0 && !saving;

  const handleContinue = async () => {
    if (!canSubmit) return;
    if (!token) {
      setError('Your session expired — please refresh and try again.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // One call sets both the owner's display name and the workspace name
      // (same endpoint the Team modal's "Save" uses).
      await apiFetch('/auth/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        authToken: token,
        body: JSON.stringify({ user_name: name.trim(), container_name: workspace.trim() }),
      });
      onDone(false);
    } catch {
      setError("Couldn't save those — please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="setup-screen">
      <div className="setup-content">
        <div className="setup-logo" aria-hidden="true">
          <div className="setup-logo-glow" />
          <img src="/favicon.svg" alt="" className="setup-logo-img" />
        </div>
        <h2>{isTeam ? 'Welcome to your new team workspace' : 'Welcome to your new workspace'}</h2>
        <p style={{ margin: '0 0 22px', color: 'var(--color-text-muted, #6b7167)', fontSize: 15, lineHeight: 1.5 }}>
          A couple of quick details so Xavior knows who you are and what to call this space.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', maxWidth: 360, margin: '0 auto', textAlign: 'left' }}>
          <input
            type="text"
            value={name}
            onChange={(e) => { setName(e.target.value); setError(null); }}
            placeholder="Your name"
            autoFocus
            style={inputStyle}
          />
          <input
            type="text"
            value={workspace}
            onChange={(e) => { setWorkspace(e.target.value); setError(null); }}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void handleContinue(); }}
            placeholder={isTeam ? 'Workspace name (e.g. Acme Team)' : "Workspace name (e.g. Sam's Workspace)"}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={handleContinue}
            disabled={!canSubmit}
            style={{
              marginTop: 4,
              padding: '12px 16px',
              borderRadius: 10,
              border: 'none',
              background: 'var(--color-brand, #15a5e3)',
              color: '#fff',
              fontSize: 15,
              fontWeight: 600,
              fontFamily: 'inherit',
              cursor: canSubmit ? 'pointer' : 'default',
              opacity: canSubmit ? 1 : 0.5,
              transition: 'opacity 0.15s',
            }}
          >
            {saving ? 'Saving…' : 'Continue'}
          </button>
          {error && (
            <p style={{ margin: 0, color: 'var(--color-danger, #EA4335)', fontSize: 13 }}>{error}</p>
          )}
          <button
            type="button"
            onClick={() => onDone(true)}
            disabled={saving}
            style={{
              alignSelf: 'center',
              marginTop: 2,
              background: 'none',
              border: 'none',
              padding: '4px 8px',
              fontFamily: 'inherit',
              fontSize: 13,
              color: 'var(--color-text-muted, #6b7167)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Skip for now
          </button>
        </div>
      </div>
      <SetupScreenStyles />
    </div>
  );
}
